import React, { createContext, useMemo, useContext } from 'react';
import { useIntl } from 'react-intl';
import get from 'lodash/get';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import { PortalConfigContext, getConfig } from '../../../config/portal';
import { getPortalName, getCurrentLocale } from '../../../utils/language';
import DetailsSection from '../DetailsSection';
import DetailsSectionVariant1 from '../DetailsSection/Variant1';
import { getCustomUom } from '../../../utils/uomHelper';
import { adsConfig } from './resources/adsConfig';
import { getImmersiveTour } from '../../../utils/immersiveTourHelper';
import { useCookies } from 'react-cookie';
import { getBoatUrl } from '../../../utils/urlHelpers/boat';
import { syncTranslationElements } from '../../../utils/translations/translationsSync';
import {
  addResizeParams, get360Photos,
  getMediaImages,
  getMediaVideos,
  getTitle
} from '../../../utils/listingHelper';
import {asArray} from '../../../utils';


export const BDPContext = createContext();
const BDPContextProvider = BDPContext.Provider;

const { messages, t } = syncTranslationElements();

const mediaImagesFormatter = (resize, fullListingName) => ({ title, original, url, date }) => {
  const src = addResizeParams(
    original ? original.url : url,
    resize,
    date.modified,
    false
  );
  const srcset = `
    ${addResizeParams(
      original ? original.url : url,
      { width: 300, height: 222 },
      date.modified,
      false
    )} 300w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 400, height: 267 },
      date.modified,
      false
    )} 400w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 700, height: 466 },
      date.modified,
      false
    )} 700w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 969, height: 646 },
      date.modified,
      false
    )} 969w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 800, height: 533 },
      date.modified,
      false
    )} 1200w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 1028, height: 685 },
      date.modified,
      false
    )} 1600w
  `;
  const sizes = `
    (max-width: 300px) 300px,
    (max-width: 400px) 400px,
    (max-width: 700px) 700px,
    (max-width: 969px) 969px,
    (max-width: 1200px) 1040px,
    1600px
  `;
  return {
    alt: title || fullListingName,
    src,
    srcset,
    sizes
  };
};

const videoFormatter = (mediaVideo, title) => ({
  title: title,
  src: mediaVideo.videoEmbedUrl?.replace(/\/v\//i, '/embed/'),
  mediaType: mediaVideo.mediaType,
  overlay: true
});

const image360Formatter = (image360, title, i) => {
  // replace all blank space from title
  title = title.replace(/\s/g, '-');
  return {
    id: `360-${title}-${i}`,
    src: image360.url?.replace('LARGE', 'XLARGE'),
    mediaType: '360Image'
  };
};

const orderFormattedMediaItems = (formattedMediaImages, formattedMediaVideos, formatted360) => {
  const [head, ...tail] = formattedMediaImages;
  let firstImage = asArray(head);
  let videos = asArray(formattedMediaVideos);
  let images360 = asArray(formatted360);
  const items = [...firstImage, ...videos, ...images360, ...tail];
  return items;
};

const formatMediaItems = (
  mediaImages,
  mediaVideos,
  images360,
  fullListingName,
  resize
) => {
  const imageFormatter = mediaImagesFormatter(resize, fullListingName);
  const videoTitle = mediaVideos?.title || fullListingName;
  const formattedMediaImages = mediaImages.map(imageFormatter);
  const formattedMediaVideos = mediaVideos.map((mediaVideo) => videoFormatter(mediaVideo, videoTitle));
  const formattedMedia360 = images360.map((img360, i) => image360Formatter(img360, fullListingName, i));
  const items = orderFormattedMediaItems(formattedMediaImages, formattedMediaVideos, formattedMedia360);
  return { formattedMediaImages, formattedMediaVideos, mediaItems: items };
};

const parseListing = (listing) => {
  const url = getBoatUrl(listing);
  const mediaImages = getMediaImages(listing);
  const mediaVideos = getMediaVideos(listing);
  const images360 = get360Photos(listing);
  const listingTitle = getTitle(listing);
  const virtualTourUrl = get(listing, 'virtualTour.0.url');
  const oemDetails = listing.oemDetails;
  return {
    url,
    mediaImages,
    mediaVideos,
    images360,
    listingTitle,
    virtualTourUrl,
    oemDetails
  };
};

const parseBdpProps = (props, context, intl, cookies) => {
  const { listing } = props;
  const parsedListing = parseListing(listing);
  const resize = props.resize || {};
  const { mediaImages, mediaVideos, images360, listingTitle } = parsedListing;
  const carouselItems = formatMediaItems(
    mediaImages,
    mediaVideos,
    images360,
    listingTitle,
    resize
  );
  const uom = get(context, 'uom.length.abbr');
  const seoConfig = get(context, 'pages.details.seo');
  const customMeta = get(context, 'supports.customMeta', false);
  const showThirdAdbox = get(context, 'supports.showThirdAdBox', false);
  const portal = getPortalName(context);
  const revealDealerPhoneNumber = get(
    context,
    `languages.${intl.locale}.revealDealerPhoneNumber`,
    false
  );
  const enableDealerLocatorV2 = get(
    context,
    'supports.enableDealerLocatorV2',
    false
  );
  const showBottomAdBox = get(context, 'supports.showBottomAdBox', false);
  const supportsSavedSearchOnBDP = get(
    context,
    'supports.savedSearchOnBDP',
    false
  );

  const mapDetailsSectionVariants = () => {
    const subsectionOrder = get(context, 'supports.subsectionOrder', 0);
    switch (subsectionOrder) {
      case 1:
        return DetailsSectionVariant1;
      default:
        return DetailsSection;
    }
  };

  const getCustomUom2 = () => {
    const isDefaultLocaleUom = get(context, 'supports.defaultUoms', false);
    const supportedUoms = get(context, 'supportedUoms');
    const defaultLocaleUom = get(
      context,
      `languages.${intl.locale}.defaultUom`
    );
    return getCustomUom(
      isDefaultLocaleUom,
      cookies,
      supportedUoms,
      defaultLocaleUom
    );
  };

  const getPageAdsConfig = () => {
    const portalAdsConfig = get(context, 'pages.details.adsConfig', {});
    const selectedAds = Object.keys(portalAdsConfig).reduce(
      (resultAds, adsKey) => {
        resultAds[adsKey] = adsConfig[adsKey];
        return resultAds;
      },
      {}
    );
    return merge(portalAdsConfig, selectedAds);
  };

  const hasImmersiveTour = get(context, 'supports.immersiveTour', false);
  const showImmersiveTour = hasImmersiveTour && !!getImmersiveTour(listing);

  const locale = getCurrentLocale();

  const supportsTridentCalculator = get(
    getConfig(),
    `languages.${locale}.supports.tridentCalculator`,
    false
  );

  const supportsMonthlyPayment = get(
    getConfig(),
    `languages.${locale}.supports.monthlyPayment`,
    false
  );

  const buttonThumbnail = {
    buttonText: `${t(messages.viewCarouselPhotos.view)} ${mediaImages.length} ${
      mediaImages.length > 1 ? t(messages.viewCarouselPhotos.photos) : t(messages.viewCarouselPhotos.photo)
    }`,
    src: carouselItems.formattedMediaImages[0]?.src,
    srcset: carouselItems.formattedMediaImages[0]?.srcset,
    sizes: carouselItems.formattedMediaImages[0]?.sizes,
    show: mediaImages.length > 0
  };

  const carouselThumbnails = [
    buttonThumbnail,
    ...carouselItems.mediaItems
  ];

  return {
    listing,
    uom,
    seoConfig,
    customMeta,
    showThirdAdbox,
    portal,
    revealDealerPhoneNumber,
    enableDealerLocatorV2,
    showBottomAdBox,
    supportsSavedSearchOnBDP,
    showImmersiveTour,
    customUom: getCustomUom2(),
    pageAdsConfig: getPageAdsConfig(),
    DetailsSectionVariant: mapDetailsSectionVariants(),
    showManufacturerForm: false,
    supportsTridentCalculator,
    supportsMonthlyPayment,
    carouselItems,
    carouselThumbnails,
    ...parsedListing
  };
};

const BoatDetailsPage = ({ children, ...props }) => {
  const intl = useIntl();
  const [cookies] = useCookies();

  const portalConfigContext = useContext(PortalConfigContext);
  const bdpContext = useMemo(
    () => parseBdpProps(props, portalConfigContext, intl, cookies),
    [props]
  );

  return <BDPContextProvider value={bdpContext}>{children}</BDPContextProvider>;
};

BoatDetailsPage.propTypes = {
  children: PropTypes.any
};

export default BoatDetailsPage;
